import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { z } from 'zod';
import { Plus, MoreHorizontal, Loader2, Search, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/data-table/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';

import {
  useGroups, useCreateGroup, useUpdateGroup, useDeleteGroup, type Group,
} from '@/hooks/use-groups';
import { ApiError } from '@/lib/api-client';
import { toast } from '@/hooks/use-toast';
import { shortId, relativeTime } from '@/lib/utils';

export const Route = createFileRoute('/_app/groups')({
  component: GroupsPage,
});

function GroupsPage() {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [editing, setEditing] = React.useState<Group | null>(null);
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useGroups({ page, pageSize: 20, search: debouncedSearch || undefined });

  return (
    <>
      <PageHeader
        title="Groups"
        description="Organize technicians into groups for scoped access to devices."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            <span>New group</span>
          </Button>
        }
      />

      <Card className="mb-4 p-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search groups"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-8"
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Devices</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>ID</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="h-32 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="h-32 text-center text-muted-foreground">No groups yet.</TableCell></TableRow>
            ) : (
              data?.data.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{g.name}</span>
                      {g.description && <span className="text-xs text-muted-foreground">{g.description}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="tabular-nums">{g._count.members}</TableCell>
                  <TableCell className="tabular-nums">{g._count.devices}</TableCell>
                  <TableCell className="text-sm text-muted-foreground tabular-nums">{relativeTime(g.createdAt)}</TableCell>
                  <TableCell><span className="id-pill">{shortId(g.id)}</span></TableCell>
                  <TableCell>
                    <GroupRowMenu group={g} onEdit={() => setEditing(g)} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {data && (
          <div className="border-t">
            <Pagination page={page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <GroupDialog open={creating} onOpenChange={setCreating} mode="create" />
      <GroupDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} mode="edit" group={editing} />
    </>
  );
}

function GroupRowMenu({ group, onEdit }: { group: Group; onEdit: () => void }) {
  const deleteGroup = useDeleteGroup();
  function handleDelete(): void {
    if (!window.confirm(`Delete group "${group.name}"?`)) return;
    deleteGroup.mutate(group.id, {
      onSuccess: () => toast({ title: 'Group deleted' }),
      onError: (err) =>
        toast({
          variant: 'destructive',
          title: 'Delete failed',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
    });
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon"><MoreHorizontal /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-32">
        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
        <DropdownMenuItem destructive onSelect={handleDelete}>
          <Trash2 />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const groupSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
});
type GroupValues = z.infer<typeof groupSchema>;

function GroupDialog({
  open, onOpenChange, mode, group,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  mode: 'create' | 'edit';
  group?: Group | null;
}) {
  const create = useCreateGroup();
  const update = useUpdateGroup();
  const form = useForm<GroupValues>({ resolver: zodResolver(groupSchema) });

  React.useEffect(() => {
    if (open) {
      form.reset({
        name: group?.name ?? '',
        description: group?.description ?? '',
      });
    }
  }, [open, group, form]);

  function onSubmit(values: GroupValues): void {
    const success = (): void => {
      toast({ title: mode === 'create' ? 'Group created' : 'Group updated' });
      onOpenChange(false);
    };
    const failure = (err: unknown): void =>
      toast({
        variant: 'destructive',
        title: mode === 'create' ? 'Could not create' : 'Could not update',
        description: err instanceof ApiError ? err.message : 'Try again',
      });

    if (mode === 'create') {
      create.mutate(values, { onSuccess: success, onError: failure });
    } else if (group) {
      update.mutate({ id: group.id, input: values }, { onSuccess: success, onError: failure });
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New group' : 'Edit group'}</DialogTitle>
          <DialogDescription>
            Groups let you scope which technicians can access which devices.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="g-name">Name</Label>
            <Input id="g-name" {...form.register('name')} autoFocus />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="g-desc">Description</Label>
            <Input id="g-desc" {...form.register('description')} placeholder="Optional" />
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="animate-spin" />}
              {mode === 'create' ? 'Create group' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
